<template>
  <div id="wrapper" class="flex h-screen bg-gradient-to-r from-[#f6f9ff] to-[#f6f9ff] dark:from-gray-900 dark:to-gray-950 overflow-x-hidden">
    <!-- Overlay for mobile -->
    <div v-if="isMenuToggled" @click="menuToggleBt" class="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden">
    </div>

    <!-- Sidebar -->
    <div id="sidebar-wrapper"
      class="fixed h-screen bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transition-all duration-300 overflow-y-auto overflow-x-hidden z-50" :class="[
        isMenuToggled ? 'w-60 translate-x-0' : 'w-16 lg:translate-x-0 -translate-x-full',
      ]">
      <div class="py-6 border-b border-gray-200 dark:border-gray-700">
        <div class="flex justify-center items-center">
          <span class="font-bold text-gray-800 dark:text-gray-300 text-lg whitespace-nowrap"
            :class="{ 'text-transparent': !isMenuToggled }">
            魔语管理平台
          </span>
        </div>
      </div>

      <div class="mt-3">
        <div v-for="item in visibleMenuItems" :key="item.path || item.name" class="text-gray-600 dark:text-gray-200">
          <!-- Regular Menu Item -->
          <router-link v-if="!item.isGroup" :to="item.path"
            class="flex items-center px-6 py-4 hover:bg-blue-100 dark:hover:bg-gray-700 transition-colors" :class="{
              'bg-indigo-50 text-indigo-700 font-bold dark:bg-indigo-900/50 dark:text-indigo-200 dark:font-bold':
                activeMenuPath == item.path,
            }" @click="isMobile && menuToggleBt()">
            <div class="w-8 flex justify-center">
              <font-awesome-icon :icon="item.icon" class="text-xl" />
            </div>
            <span class="text-base ml-3 whitespace-nowrap" :class="{ 'hidden': !isMenuToggled }">
              {{ item.name }}
            </span>
          </router-link>

          <!-- Group Item -->
          <div v-else class="relative">
            <div @click="toggleGroup(item.name)"
              class="flex items-center px-6 py-4 hover:bg-blue-100 dark:hover:bg-gray-700 transition-colors cursor-pointer">
              <div class="w-8 flex justify-center">
                <font-awesome-icon :icon="item.icon" class="text-xl" />
              </div>
              <span class="text-base ml-3 flex-grow whitespace-nowrap" :class="{ 'hidden': !isMenuToggled }">
                {{ item.name }}
              </span>
              <div v-if="isMenuToggled" class="w-5 h-5">
                <ChevronDownIcon v-if="isGroupExpanded(item.name)" class="w-5 h-5" />
                <ChevronRightIcon v-else class="w-5 h-5" />
              </div>
            </div>

            <!-- Group Subitems -->
            <div v-if="isGroupExpanded(item.name) && isMenuToggled"
              class="pl-4 bg-gray-50 dark:bg-gray-900/60 border-y border-gray-200 dark:border-gray-700">
              <router-link v-for="subItem in item.items" :key="subItem.path" :to="subItem.path"
                class="flex items-center px-6 py-3 hover:bg-blue-100 dark:hover:bg-gray-700 transition-colors" :class="{
                  'bg-indigo-50 text-indigo-700 font-bold dark:bg-indigo-900/50 dark:text-indigo-200 dark:font-bold':
                    activeMenuPath == subItem.path,
                }" @click="isMobile && menuToggleBt()">
                <div class="w-8 flex justify-center">
                  <font-awesome-icon :icon="subItem.icon" class="text-lg" />
                </div>
                <span class="text-sm ml-3 flex-1">{{ subItem.name }}</span>
                <span
                  v-if="getMenuBadgeCount(subItem) > 0"
                  class="ml-2 min-w-[1.25rem] rounded-full bg-rose-600 px-1.5 py-0.5 text-center text-xs font-bold leading-5 text-white"
                >
                  {{ formatMenuBadgeCount(getMenuBadgeCount(subItem)) }}
                </span>
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="flex-1 " :class="[isMenuToggled ? 'lg:ml-60' : 'lg:ml-16']">

      <div class="h-[78px] border-b border-gray-200 dark:border-gray-700 flex items-center px-5 bg-white dark:bg-gray-800">
        <div class="flex justify-between w-full">
          <div class="flex items-center dark:text-gray-200">
            <button class="border-0 bg-transparent cursor-pointer" @click="menuToggleBt">
              <font-awesome-icon :icon="isMenuToggled
                ? ['fas', isMobile ? 'times' : 'align-left']
                : ['fas', isMobile ? 'bars' : 'align-right']" class="text-2xl text-gray-800 dark:text-gray-200" />
            </button>
            <h4 class="text-xl font-bold ml-4 dark:text-gray-200">
              {{ calCurrentItem }}
            </h4>
          </div>
          <div class="flex items-center space-x-5">
            <ThemeToggle />
            <Avatar />
          </div>
        </div>
      </div>

      <!-- Main View Content -->
      <div class="p-5">
        <router-view />
      </div>
    </div>

    <!-- Toast Container -->
    <ToastContainer />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import { useRoute } from "vue-router";
import Avatar from "@/components/Avatar.vue";
import ThemeToggle from "@/components/ui/ThemeToggle.vue";
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/vue/24/outline';
import ToastContainer from "@/components/ui/ToastContainer.vue";
import { useAuthStore } from "@/stores/auth";
import { getAdminTutorProfileReviewsAPI } from "@/api/admin/tutorProfileReview";

const route = useRoute();
const authStore = useAuthStore();
const expandedGroups = ref({});
const pendingTutorReviewCount = ref(0);

const menuItems = ref([
  // dashboard
  { icon: ['fas', 'star'], name: "总览", path: "/" },
  { icon: ['fas', 'users'], name: "用户管理", path: "/user-manager" },
  { icon: ['fas', 'bullhorn'], name: "公告管理", path: "/announcement" },
  {
    icon: ['fas', 'mobile-screen'],
    name: "APP 管理",
    isGroup: true,
    items: [
      {
        name: "营销素材",
        path: "/promo",
        icon: ['fas', 'photo-film']
      },
      {
        name: "版本控制",
        path: "/app/version",
        icon: ['fas', 'code-branch']
      },
      {
        name: "热更新",
        path: "/app/ota",
        icon: ['fas', 'cloud-upload-alt']
      }
    ]
  },
  { icon: ['fas', 'envelope'], name: "站内信管理", path: "/message" },
  {
    icon: ['fas', 'comments'],
    name: "口语管理",
    isGroup: true,
    items: [
      {
        name: "话题管理",
        path: "/topic-manager",
        icon: ['fas', 'list']
      },
      {
        name: "串题组管理",
        path: "/topic-group",
        icon: ['fas', 'link']
      },
      {
        name: "新增话题检测",
        path: "/topic-detector",
        icon: ['fas', 'search']
      }
    ]
  },
  {
    icon: ['fas', 'file-alt'],
    name: "Mock 管理",
    isGroup: true,
    items: [
      {
        name: "试卷管理",
        path: "/mock/paper-management",
        icon: ['fas', 'file-alt']
      },
      {
        name: "分类管理",
        path: "/mock/category-management",
        icon: ['fas', 'layer-group']
      },
      {
        name: "书籍管理",
        path: "/mock/book-management",
        icon: ['fas', 'book']
      }
    ]
  },
  {
    icon: ['fas', 'book-open'],
    name: "辞书管理",
    isGroup: true,
    items: [
      {
        name: "词汇书籍",
        path: "/dictation/books",
        icon: ['fas', 'book']
      },
      {
        name: "对话书籍",
        path: "/dictation/dialogue/books",
        icon: ['fas', 'headphones']
      },
      {
        name: "分类管理",
        path: "/dictation/categories",
        icon: ['fas', 'layer-group']
      },
      {
        name: "内容管理",
        path: "/dictation/contents",
        icon: ['fas', 'spell-check']
      }
    ]
  },
  {
    icon: ['fas', 'crown'],
    name: "会员管理",
    isGroup: true,
    items: [
      {
        name: "套餐分类管理",
        path: "/category-manager",
        icon: ['fas', 'layer-group']
      },
      {
        name: "套餐管理",
        path: "/plan-manager",
        icon: ['fas', 'box']
      },
      {
        name: "功能管理",
        path: "/feature-manager",
        icon: ['fas', 'cog']
      },
      {
        name: "兑换码管理",
        path: "/redeem-code-manager",
        icon: ['fas', 'ticket']
      }
    ]
  },
  {
    icon: ['fas', 'gift'],
    name: "推荐管理",
    isGroup: true,
    items: [
      {
        name: "佣金策略",
        path: "/referral/policies",
        icon: ['fas', 'sliders-h']
      },
      {
        name: "佣金记录",
        path: "/referral/commissions",
        icon: ['fas', 'coins']
      },
      {
        name: "提现管理",
        path: "/referral/withdrawals",
        icon: ['fas', 'money-bill-wave']
      }
    ]
  },
  {
    icon: ['fas', 'building'],
    name: "租户运营",
    isGroup: true,
      items: [
        {
          name: "租户管理",
          path: "/tenant-manager",
          icon: ['fas', 'link'],
          superAdminOnly: true
        },
        {
          name: "租户月结",
          path: "/tenant-settlements",
          icon: ['fas', 'coins'],
          superAdminOnly: true
        },
        {
          name: "平台导师结算",
          path: "/platform-tutor-settlements",
          icon: ['fas', 'money-bill-wave'],
          superAdminOnly: true
        },
        {
          name: "教师开课审核",
          path: "/tutor-profile-reviews",
          icon: ['fas', 'check-circle'],
          badgeKey: "pendingTutorReviews",
          superAdminOnly: true
        },
        {
          name: "约课退款处理",
          path: "/tutor-refunds",
          icon: ['fas', 'money-bill-wave'],
          superAdminOnly: true
        },
      ]
  },
  {
    icon: ['fas', 'cogs'],
    name: "系统管理",
    isGroup: true,
      items: [
        {
          name: "定时任务管理",
          path: "/scheduled-tasks",
        icon: ['fas', 'tasks']
      },
      {
        name: "系统日志",
        path: "/system-logs",
        icon: ['fas', 'file-alt']
      }
    ]
  }
]);

const visibleMenuItems = computed(() => {
  return menuItems.value.reduce((result, item) => {
    if (item.isGroup) {
      const visibleItems = item.items.filter((subItem) => !subItem.superAdminOnly || authStore.isSuperAdmin);
      if (visibleItems.length > 0) {
        result.push({ ...item, items: visibleItems });
      }
      return result;
    }

    if (!item.superAdminOnly || authStore.isSuperAdmin) {
      result.push(item);
    }

    return result;
  }, []);
});

const getMenuBadgeCount = (item) => {
  if (item.badgeKey === "pendingTutorReviews") {
    return pendingTutorReviewCount.value;
  }
  return 0;
};

const formatMenuBadgeCount = (count) => {
  return count > 99 ? "99+" : String(count);
};

const loadPendingTutorReviewCount = async () => {
  if (!authStore.isSuperAdmin) {
    pendingTutorReviewCount.value = 0;
    return;
  }
  try {
    const res = await getAdminTutorProfileReviewsAPI({
      page: 1,
      limit: 1,
      status: "pending",
      tenantId: null,
    });
    if (res.status === 200 && res.data) {
      pendingTutorReviewCount.value = res.data.total;
    }
  } catch (error) {
    console.error("加载教师开课待审核数量失败", error);
  }
};

const isMenuToggled = ref(false);
const isMobile = ref(false);
// 记录上一次断点状态，仅在跨越断点时自动切换，保留用户手动操作
let wasMobile = true;
let resizeTimer = null;

const toggleGroup = (groupName) => {
  // 侧边栏收起时点击分组，先展开侧边栏
  if (!isMenuToggled.value) {
    isMenuToggled.value = true;
    // 确保该分组展开
    expandedGroups.value[groupName] = true;
    return;
  }
  expandedGroups.value[groupName] = !expandedGroups.value[groupName];
};

const isGroupExpanded = (groupName) => {
  return expandedGroups.value[groupName];
};

/**
 * 判断菜单路径是否与当前路由匹配
 */
const isPathActive = (menuPath) => {
  if (!menuPath) return false;
  const currentPath = route.path;
  return currentPath === menuPath || (menuPath !== '/' && currentPath.startsWith(`${menuPath}/`));
};

/**
 * 当前激活的菜单项
 */
const activeMenuItem = computed(() => {
  for (const item of visibleMenuItems.value) {
    if (item.isGroup) {
      const foundItem = item.items.find(({ path }) => isPathActive(path));
      if (foundItem) return foundItem;
    } else if (isPathActive(item.path)) {
      return item;
    }
  }
  return null;
});

/**
 * 当前激活菜单路径（用于侧边栏高亮）
 */
const activeMenuPath = computed(() => activeMenuItem.value?.path || "");

/**
 * 当前激活菜单名称（用于顶部标题）
 */
const calCurrentItem = computed(() => activeMenuItem.value?.name || "");

// 根据当前路由自动展开所属分组
const expandGroupForRoute = () => {
  for (const item of visibleMenuItems.value) {
    if (item.isGroup) {
      const match = item.items.find(({ path }) => isPathActive(path));
      if (match) {
        expandedGroups.value[item.name] = true;
      }
    }
  }
};

const menuToggleBt = () => {
  isMenuToggled.value = !isMenuToggled.value;
};

// 仅在跨越断点时自动切换侧边栏状态，防抖 100ms
const handleResize = () => {
  if (resizeTimer) clearTimeout(resizeTimer);
  resizeTimer = setTimeout(() => {
    const nowMobile = window.innerWidth < 1024;
    isMobile.value = nowMobile;

    // 仅在断点跨越时自动切换，不覆盖用户手动操作
    if (wasMobile && !nowMobile) {
      // 从移动端进入桌面端：展开侧边栏
      isMenuToggled.value = true;
    } else if (!wasMobile && nowMobile) {
      // 从桌面端进入移动端：收起侧边栏
      isMenuToggled.value = false;
    }
    wasMobile = nowMobile;
  }, 100);
};

// 路由变化时自动展开当前路由所属分组
watch(() => route.path, () => {
  expandGroupForRoute();
  if (route.path.startsWith("/tutor-profile-reviews")) {
    loadPendingTutorReviewCount();
  }
});

onMounted(() => {
  // 初始化断点状态
  const nowMobile = window.innerWidth < 1024;
  isMobile.value = nowMobile;
  wasMobile = nowMobile;
  isMenuToggled.value = !nowMobile;

  // 初始化时展开当前路由所属分组
  expandGroupForRoute();
  loadPendingTutorReviewCount();

  window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
  if (resizeTimer) clearTimeout(resizeTimer);
});
</script>

<style scoped>
#wrapper {
  overflow-x: hidden;
}

#sidebar-wrapper {
  z-index: 1000;
}
</style>
